import React, { ReactNode, RefObject, useEffect, useId, useMemo, useRef } from 'react';
import Modal from 'react-modal';
import clsx from 'clsx';
import styles from './ModalDialog.module.scss';

export interface ModalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  children: ReactNode;
  actions?: ReactNode;
  headerActions?: ReactNode;
  role?: 'dialog' | 'alertdialog';
  triggerRef?: RefObject<HTMLElement | HTMLButtonElement | null>;
  initialFocusRef?: RefObject<HTMLElement>;
  shouldCloseOnOverlayClick?: boolean;
  shouldCloseOnEsc?: boolean;
  contentClassName?: string;
  overlayClassName?: string;
}

export default function ModalDialog({
  isOpen,
  onClose,
  title,
  description,
  ariaLabelledBy,
  ariaDescribedBy,
  children,
  actions,
  headerActions,
  role = 'dialog',
  triggerRef,
  initialFocusRef,
  shouldCloseOnOverlayClick = true,
  shouldCloseOnEsc = true,
  contentClassName,
  overlayClassName,
}: ModalDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  const ariaProps = useMemo(
    () => ({
      labelledby: ariaLabelledBy ?? (title ? titleId : undefined),
      describedby: ariaDescribedBy ?? (description ? descriptionId : undefined),
    }),
    [ariaLabelledBy, ariaDescribedBy, title, description, titleId, descriptionId]
  );

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleAfterOpen = () => {
    if (initialFocusRef?.current) {
      initialFocusRef.current.focus();
      return;
    }

    const focusTarget = dialogRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusTarget?.focus();
  };

  const handleAfterClose = () => {
    triggerRef?.current?.focus();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      onAfterOpen={handleAfterOpen}
      onAfterClose={handleAfterClose}
      className={clsx(styles.modalContent, contentClassName)}
      overlayClassName={clsx(styles.modalOverlay, overlayClassName)}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      shouldFocusAfterRender={true}
      shouldReturnFocusAfterClose={false}
      ariaHideApp={false}
      role={role}
      aria={ariaProps}
    >
      <div ref={dialogRef} className={styles.dialog}>
        {(title || headerActions) && (
          <div className={styles.header}>
            {title ? (
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
            ) : (
              <span />
            )}
            {headerActions}
          </div>
        )}

        {description && (
          <div id={descriptionId} className={styles.description}>
            {description}
          </div>
        )}

        <div className={styles.body}>{children}</div>

        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
    </Modal>
  );
}
